import React, {
  createRef,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import useTendering from "../../../hooks/useTendering";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, EffectFade } from "swiper/modules";
import useUsers from "../../../hooks/useUsers";
import useCompanies from "../../../hooks/useCompanies";
import { ReactComponent as PlaySvg } from "../../../assets/icons/play.svg";

const assetsUri = import.meta.env.ASSETS_URI;

import "swiper/css";
import "swiper/css/effect-fade";
import _ from "lodash";
import AppContext from "../../../AppContext";
import { formatNumber } from "../../../helpers/common.helper";
import StylishButton from "../../../components/common/StylishButton";
import { useNavigate } from "react-router-dom";
import ScrollDownIndicator from "../../../components/common/ScrollDownIndicator";

const Recommend = (props) => {
  const { designStyles } = useContext(AppContext);
  const { query } = useTendering();
  const { queryUsers } = useUsers();
  const { queryCompany } = useCompanies();
  const [datasource, setDatasource] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(undefined);
  const [nextSlideIndex, setNextSlideIndex] = useState(undefined);
  const navigate = useNavigate();
  const swiperRef = useRef();

  const fetchData = async () => {
    const { _metadata, data } = await query(
      { active: true, deleted: false },
      1,
      4,
      null,
      { createdOn: -1 }
    );

    // * Get owner
    const userIds = data.map((u) => u.createdBy);
    const companyIds = data.map((u) => u.company).map((d) => !!d);

    const [userResults, companyResults] = await Promise.allSettled([
      queryUsers({ _id: { $in: userIds } }, null, -1, {
        _id: 1,
        firstname: 1,
        surname: 1,
        avatar: 1,
        uri: 1,
      }),
      queryCompany({ _id: { $in: userIds } }, null, -1, {
        _id: 1,
        logo: 1,
        name: 1,
      }),
    ]);

    setDatasource(
      data.map((d) => {
        // * Map user or company
        const _user = userResults.value.data.find(
          (user) => user._id === d.createdBy
        );

        // TODO: Map for company

        d.createdBy = _user;

        return d;
      })
    );

    setCurrentIndex(0);
    setNextSlideIndex(1);
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div className="tendering__landing__recommend">
      <div
        className="tendering__landing__recommend__left"
        style={{
          display: "flex",
          flexDirection: "column",
          height: "90%",
          gridColumn: 1,
          gridRow: 1,
        }}
      >
        <h2 className="tendering__landing__recommend__title">Recommend</h2>
        {currentIndex !== undefined && (
          <>
            <div className="tendering__landing__recommend__current">
              <h3 className="tendering__landing__recommend__current__title">
                {datasource[currentIndex].name}
              </h3>
              <div className="tendering__landing__recommend__current__detail">
                <span className="tendering__landing__recommend__current__detail__name">
                  Scope of Work
                </span>
                <span className="tendering__landing__recommend__current__detail__value">
                  {
                    designStyles.works.find(
                      (w) => w.value === datasource[currentIndex].work
                    )?.name
                  }
                </span>
                <span className="tendering__landing__recommend__current__detail__name">
                  Type of Space
                </span>
                <span className="tendering__landing__recommend__current__detail__value">
                  {
                    designStyles.spaces.find(
                      (w) => w.value === datasource[currentIndex].space
                    )?.name
                  }
                </span>
                <span className="tendering__landing__recommend__current__detail__name">
                  Styles
                </span>
                <span className="tendering__landing__recommend__current__detail__value">
                  {datasource[currentIndex].styles.map((style) => (
                    <span className="tendering__landing__recommend__current__detail__value__tag">
                      {designStyles.styles.find((w) => w.value === style)?.name}
                    </span>
                  ))}
                </span>
                <span className="tendering__landing__recommend__current__detail__name">
                  Location
                </span>
                <span className="tendering__landing__recommend__current__detail__value">
                  {datasource[currentIndex].location}
                </span>
                <span className="tendering__landing__recommend__current__detail__name">
                  Size
                </span>
                <span className="tendering__landing__recommend__current__detail__value">
                  250 sq.m.
                </span>
                {datasource[currentIndex].budget && (
                  <>
                    <span className="tendering__landing__recommend__current__detail__name">
                      Budget
                    </span>
                    <span className="tendering__landing__recommend__current__detail__value">
                      {formatNumber(datasource[currentIndex].budget, "THB")}
                    </span>
                  </>
                )}
              </div>
            </div>
            <div className="tendering__landing__recommend__footer">
              <StylishButton
                label={"View now"}
                icon={<PlaySvg />}
                onClick={() => navigate(datasource[currentIndex]._id)}
                size="small"
              />
            </div>
          </>
        )}
      </div>
      <div
        className="tendering__landing__recommend__right"
        style={{
          display: "grid",
          justifyItems: "start",
          alignItems: "end",
          gridColumn: 2,
          gridRow: 1,
        }}
      >
        {nextSlideIndex !== undefined && datasource?.length > 1 && (
          <div
            className="tendering__landing__recommend__covers__slide__next"
            onClick={() => {
              if (!swiperRef.current) return;
              swiperRef.current.swiper.slideNext();
            }}
          >
            <img
              className="tendering__landing__recommend__covers__slide__next__image"
              src={`${assetsUri}/${
                datasource[nextSlideIndex].images.cover.cropped ??
                datasource[nextSlideIndex].images.cover.original
              }`}
            />
            <div className="tendering__landing__recommend__covers__slide__next__overlay" />
          </div>
        )}
        <Swiper
          ref={swiperRef}
          className="tendering__landing__recommend__covers"
          effect={"fade"}
          modules={[EffectFade, Pagination]}
          slidesPerView={1}
          pagination={{
            clickable: true,
            el: ".tendering__landing__recommend__pagination",
            bulletClass: "tendering__landing__recommend__pagination__bullet",
            bulletActiveClass:
              "tendering__landing__recommend__pagination__bullet--active",
            renderBullet: function (index, className) {
              return '<span class="' + className + '">' + "</span>";
            },
          }}
          onActiveIndexChange={(swiper) => {
            setCurrentIndex(swiper.activeIndex);

            if (swiper.isEnd) {
              setNextSlideIndex(0);
              return;
            }
            setNextSlideIndex(swiper.activeIndex + 1);
          }}
          rewind={true}
        >
          {datasource?.map((data, index) => (
            <SwiperSlide key={index}>
              <div className="tendering__landing__recommend__covers__slide">
                <img
                  className="tendering__landing__recommend__covers__slide__image"
                  src={`${assetsUri}/${
                    data.images.cover.cropped ?? data.images.cover.original
                  }`}
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
        <div className="tendering__landing__recommend__pagination" />
      </div>
      <div className="tendering__landing__recommend__scroll-down">
        <ScrollDownIndicator />
      </div>
    </div>
  );
};

Recommend.propTypes = {};

export default Recommend;
